import {
  DefaultNodeTypes,
  SerializedBlockNode,
  SerializedLinkNode,
} from '@payloadcms/richtext-lexical'
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'
import {
  JSXConvertersFunction,
  LinkJSXConverter,
  RichText as RichTextWithoutBlocks,
} from '@payloadcms/richtext-lexical/react'
import { TypographyJSXConverters } from 'payload-lexical-typography/converters'

import type { FAQBlock as FAQBlockType } from '@/payload-types'

import type {
  BannerBlock as BannerBlockProps,
  ImageCtaBlock as ImageCtaBlockProps,
  CallToActionBlock as CTABlockProps,
  ContentBlock as ContentBlockProps,
  ListBlock as ListBlockProps,
  Cta2Block as Cta2BlockProps,
  Cta5Block as Cta5BlockProps,
  Cta1Block as Cta1BlockProps,
  VideoBlock2 as VideoBlock2Props,
  ArchiveBlock as ArchiveBlockProps,
} from '@/payload-types'
import type { MediaBlock as MediaBlockType } from '@/payload-types'
import { BannerBlock } from '@/blocks/Banner/Component'
import { ArchiveBlockClient } from '@/blocks/ArchiveBlock/ClientComponent'
import { CallToActionBlock } from '@/blocks/CallToAction/Component'
import { ContentBlock } from '@/blocks/Content/Component'
import { ServiceAreasBlock } from '@/blocks/ListBlock/Component'
import { Cta2Block } from '@/blocks/cta2/component'
import { Cta5Block } from '@/blocks/Cta5/Component'
import { Cta1Block } from '@/blocks/Cta1/Component'
import ImageCtaBlock from '@/blocks/ImageCta/Component'
import VideoBlock2Comp from '@/blocks/videoblock2/Component'
import PratnerBlocks from '@/blocks/PratnerBlocks/Component'
import { cn } from '@/utilities/ui'
import { FAQBlockEqualColumns } from '@/blocks/Faq/Component'
import { MediaBlock as MediaBlockComponent } from '@/blocks/MediaBlock/Component'
import { Cta1 } from '@/blocks/Cta1/config'

type NodeTypes =
  | DefaultNodeTypes
  | SerializedBlockNode<
      | BannerBlockProps
      | ArchiveBlockProps
      | CTABlockProps
      | ContentBlockProps
      | ListBlockProps
      | Cta2BlockProps
      | Cta5BlockProps
      | ImageCtaBlockProps
      | Cta1BlockProps
      | VideoBlock2Props
      | MediaBlockType
      | FAQBlockType
    >

const internalDocToHref = ({ linkNode }: { linkNode: SerializedLinkNode }) => {
  const { value, relationTo } = linkNode.fields.doc!
  if (typeof value !== 'object') {
    throw new Error('Expected value to be an object')
  }
  const slug = value.slug
  return relationTo === 'posts' ? `/posts/${slug}` : `/${slug}`
}

const jsxConverters: JSXConvertersFunction<NodeTypes> = ({ defaultConverters }) => ({
  ...defaultConverters,
  ...LinkJSXConverter({ internalDocToHref }),
  ...TypographyJSXConverters,

  blocks: {
    // Wrap all blocks with consistent top spacing to match page RenderBlocks spacing
    banner: ({ node }) => (
      <div className="mt-5 lg:mt-8 first:mt-0">
        <BannerBlock className="col-start-2 mb-4" {...node.fields} />
      </div>
    ),
    archive: ({ node }) => (
      <div className="mt-5 lg:mt-8 first:mt-0 not-prose">
        <ArchiveBlockClient {...node.fields} />
      </div>
    ),
    imagecta: ({ node }) => (
      <div className="mt-5 lg:mt-8 first:mt-0">
        <ImageCtaBlock {...node.fields} />
      </div>
    ),
    cta: ({ node }) => (
      <div className="mt-5 lg:mt-8 first:mt-0">
        <CallToActionBlock {...node.fields} />
      </div>
    ),
    content: ({ node }) => (
      <div className="mt-5 lg:mt-8 first:mt-0">
        <ContentBlock {...node.fields} />
      </div>
    ),
    'list-block': ({ node }) => (
      <div className="mt-5 lg:mt-8 first:mt-0">
        <ServiceAreasBlock {...node.fields} />
      </div>
    ),
    cta2: ({ node }) => (
      <div className="mt-5 lg:mt-8 first:mt-0">
        <Cta2Block {...node.fields} />
      </div>
    ),
    cta5: ({ node }) => (
      <div className="mt-5 lg:mt-8 first:mt-0">
        <Cta5Block {...node.fields} />
      </div>
    ),
    cta1: ({ node }) => (
      <div className="mt-5 lg:mt-8 first:mt-0">
        <Cta1Block {...node.fields} />
      </div>
    ),
    videoBlock2: ({ node }) => (
      <div className="mt-5 lg:mt-8 first:mt-0">
        <VideoBlock2Comp
          title={node.fields.title as any}
          videos={(node.fields as any).videos ?? []}
        />
      </div>
    ),
    mediaBlock: ({ node }: { node: SerializedBlockNode<MediaBlockType> }) => (
      <div className="mt-5 lg:mt-8 first:mt-0">
        <MediaBlockComponent {...node.fields} />
      </div>
    ),
    pratner: ({ node }: { node: SerializedBlockNode<any> }) => (
      <div className="mt-5 lg:mt-8 first:mt-0">
        <PratnerBlocks {...node.fields} />
      </div>
    ),
    faq: ({ node }: { node: SerializedBlockNode<FAQBlockType> }) => (
      <div className="mt-5 lg:mt-8 first:mt-0">
        <FAQBlockEqualColumns addSchema={false} {...node.fields} />
      </div>
    ),
    // Fallback for any unknown blocks - just render as a div with debug info in development
    ...Object.fromEntries(
      ['listBlock', 'fAQBlock', 'youTubeEmbed'].map((blockType) => [
        blockType,
        ({ node }: { node: any }) => (
          <div className="mt-5 lg:mt-8 first:mt-0">
            <div className="my-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
              {process.env.NODE_ENV === 'development' && (
                <p className="text-sm text-gray-600 mb-2">
                  Block type: {blockType} (converter not implemented)
                </p>
              )}
              <div className="text-gray-800">Block content would render here</div>
            </div>
          </div>
        ),
      ]),
    ),
  },
})

type Props = {
  data: SerializedEditorState
  enableGutter?: boolean
  enableProse?: boolean
  // When true, remove top/bottom margins from common child elements (headings, p, lists)
  resetChildMargins?: boolean
} & React.HTMLAttributes<HTMLDivElement>

export default function RichText(props: Props) {
  const {
    className,
    enableProse = true,
    enableGutter = true,
    resetChildMargins = false,
    ...rest
  } = props
  return (
    <RichTextWithoutBlocks
      converters={jsxConverters}
      className={cn(
        'text-wrap-pretty',
        {
          'container ': enableGutter,
          'max-w-none': !enableGutter,
          // Ensure links are primary colored in prose mode too
          'mx-auto prose md:prose-md dark:prose-invert prose-a:text-primary hover:prose-a:text-primary/80 prose-a:no-underline':
            enableProse,
        },
        // Ensure both unordered and ordered list markers use the primary color
        enableProse && '[&_li]:marker:text-primary',
        // Optionally zero out margins on common descendants to avoid unexpected spacing
        resetChildMargins &&
          '[&_h1]:mt-0 [&_h1]:mb-0 [&_h2]:mt-0 [&_h2]:mb-0 [&_h3]:mt-0 [&_h3]:mb-0 [&_h4]:mt-0 [&_h4]:mb-0 [&_h5]:mt-0 [&_h5]:mb-0 [&_h6]:mt-0 [&_h6]:mb-0 [&_p]:mt-0 [&_p]:mb-0 [&_ul]:mt-0 [&_ul]:mb-0 [&_ol]:mt-0 [&_ol]:mb-0',
        // When not using prose, also force anchor colors to primary within the container
        !enableProse && '[&_a]:text-primary hover:[&_a]:text-primary/80',
        className,
      )}
      {...rest}
    />
  )
}
